// src/pages/InformativePanel.jsx
import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import {
  Sparkles,
  Edit2,
  Trash2,
  Info,
  ChevronDown,
  ChevronUp,
  Link as LinkIcon,
  Tag,
  MapPin,
} from 'lucide-react';

const API_URL =
  process.env.REACT_APP_API_URL ||
  window.__API_URL__ ||
  'http://localhost:3000';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const TIMEZONE_GROUPS = [
  {
    label: 'United States',
    zones: ['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York'],
  },
  {
    label: 'South America',
    zones: ['America/Sao_Paulo', 'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Buenos_Aires'],
  },
  {
    label: 'Europe',
    zones: ['Europe/London', 'Europe/Lisbon', 'Europe/Madrid', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Athens'],
  },
];

const defaultForm = {
  topic: '',
  label: '',
  content: '',
  keywords: '',
  // ✅ Location as user-friendly separated text (backend will normalize)
  location: '',
  active: true,
  useAI: true,
  reference: '',
  linkUrl: '',
  timezone: 'America/Los_Angeles',
  timezoneMode: 'preset', // 'preset' | 'custom'
  icalUrl: '',
};

function normalizeKey(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function splitBySeparators(raw) {
  // accepts: comma, semicolon, pipe, newline
  return String(raw || '')
    .split(/[,;|\n]+/g)
    .map((k) => k.trim())
    .filter(Boolean);
}

function isTimezoneInPreset(tz) {
  const zone = String(tz || '').trim();
  if (!zone) return false;
  return TIMEZONE_GROUPS.some((group) => group.zones.includes(zone));
}

const InformativePanel = () => {
  const [entries, setEntries] = useState([]);
  const [formData, setFormData] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  async function fetchEntries() {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/informative-panel`, {
        headers: getAuthHeaders(),
      });
      setEntries(res.data || []);
    } catch (e) {
      console.error('Error fetching informative entries:', e?.response?.data || e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEntries();
  }, []);

  const allLocationKeys = useMemo(() => {
    // debug helper badges: show known normalized keys already stored
    const set = new Set();
    for (const e of entries || []) {
      const arr = Array.isArray(e.locationKeys) ? e.locationKeys : [];
      arr.forEach((k) => set.add(normalizeKey(k)));
      if (e.locationKey) set.add(normalizeKey(e.locationKey));
      if (e.locationKeyPrimary) set.add(normalizeKey(e.locationKeyPrimary));
    }
    return Array.from(set).filter(Boolean).sort();
  }, [entries]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === 'timezoneMode') {
      setFormData((prev) => ({
        ...prev,
        timezoneMode: value,
        timezone:
          value === 'preset'
            ? prev.timezone && isTimezoneInPreset(prev.timezone)
              ? prev.timezone
              : 'America/Los_Angeles'
            : prev.timezone,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const keywordsArray = splitBySeparators(formData.keywords);

    // ✅ Keep location as user-friendly separated string.
    // Backend will parse/normalize into locationKeys/locationKeyPrimary.
    const locationText = String(formData.location || '').trim();

    const payload = {
      topic: String(formData.topic || '').trim(),
      label: String(formData.label || '').trim(),
      content: String(formData.content || '').trim(),
      keywords: keywordsArray,
      active: !!formData.active,
      useAI: !!formData.useAI,
      reference: formData.reference ? formData.reference.toLowerCase().trim() : '',
      linkUrl: formData.linkUrl || '',
      timezone: String(formData.timezone || '').trim() || 'America/Los_Angeles',
      icalUrl: formData.icalUrl || '',

      // ✅ send human field; server accepts "location" and converts to keys
      location: locationText,
    };

    try {
      if (editingId) {
        await axios.put(`${API_URL}/informative-panel/${editingId}`, payload, {
          headers: getAuthHeaders(),
        });
        
      } else {
        await axios.post(`${API_URL}/informative-panel`, payload, {
          headers: getAuthHeaders(),
        });
      }

      setFormData(defaultForm);
      setEditingId(null);
      fetchEntries();
    } catch (e2) {
      console.error('Error saving informative entry:', e2?.response?.data || e2);
      alert('Failed to save entry. Please check the console for details.');
    }
  };

  const handleEdit = (entry) => {
    const keywordsStr = Array.isArray(entry.keywords)
      ? entry.keywords.join(', ')
      : String(entry.keywords || '');

    const tz = entry.timezone || 'America/Los_Angeles';
    const inPreset = isTimezoneInPreset(tz);

    // ✅ Load location into the text field using saved keys if present
    // Prefer: locationKeys -> locationKeyPrimary -> locationKey
    let loc = '';
    if (Array.isArray(entry.locationKeys) && entry.locationKeys.length) {
      // show as user-friendly separated values (best effort)
      loc = entry.locationKeys.join('; ');
    } else if (entry.locationKeyPrimary) {
      loc = String(entry.locationKeyPrimary || '');
    } else if (entry.locationKey) {
      loc = String(entry.locationKey || '');
    }

    setFormData({
      topic: entry.topic || '',
      label: entry.label || '',
      content: entry.content || '',
      keywords: keywordsStr,
      location: loc,
      active: entry.active !== false,
      useAI: entry.useAI !== false,
      reference: entry.reference || '',
      linkUrl: entry.linkUrl || '',
      timezone: tz,
      timezoneMode: inPreset ? 'preset' : 'custom',
      icalUrl: entry.icalUrl || '',
    });

    setEditingId(entry._id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/informative-panel/${id}`, {
        headers: getAuthHeaders(),
      });
      fetchEntries();
    } catch (e) {
      console.error('Error deleting informative entry:', e?.response?.data || e);
      alert('Failed to delete entry.');
    }
  };

  const handleCancelEdit = () => {
    setFormData(defaultForm);
    setEditingId(null);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2 text-gray-800">
        <Sparkles className="text-blue-500" size={22} /> Informative Panel
      </h2>

      <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <Info className="text-blue-600" size={20} />
          </div>
          <div>
            <p className="text-sm text-blue-900 font-medium mb-1">
              Centralized knowledge used by the AI
            </p>
            <p className="text-sm text-blue-900">
              Use <span className="mx-1 font-semibold">Location</span> to keep inventory and calendars separated.
              Example: <span className="font-semibold">ceara; caponga; barra da caponga</span>.
              No fixed list, no hardcoding.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <button
          type="button"
          onClick={() => setGuideOpen((v) => !v)}
          className="w-full flex items-center justify-between rounded-lg bg-white px-4 py-3 text-left shadow-sm hover:bg-gray-50 border"
        >
          <span className="text-sm font-medium text-gray-800">
            Field guide (what each field means)
          </span>
          {guideOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {guideOpen && (
          <div className="mt-3 rounded-lg border bg-white p-4 text-sm text-gray-700">
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <span className="font-semibold">Location</span>: separated values used as location filters.
                Use <code className="px-1 bg-gray-100 rounded">,</code> <code className="px-1 bg-gray-100 rounded">;</code> or{' '}
                <code className="px-1 bg-gray-100 rounded">|</code>.
              </li>
              <li>
                Example: <code className="px-1 bg-gray-100 rounded">ceara; caponga; barra da caponga</code>
              </li>
              <li>
                The system stores this as normalized keys:
                <code className="px-1 bg-gray-100 rounded ml-1">["ceara","caponga","barra_da_caponga"]</code>
              </li>
            </ul>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white p-6 rounded-xl shadow-md mb-10 border"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Topic
            </label>
            <input
              name="topic"
              value={formData.topic}
              onChange={handleChange}
              className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200"
              placeholder="Vacation rentals, Legal intake, Cleaning, etc."
              required
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label
            </label>
            <input
              name="label"
              value={formData.label}
              onChange={handleChange}
              className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200"
              placeholder="Short name, e.g. Caponga Chalet 01"
              required
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location (separated)
            </label>
            <input
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200"
              placeholder="ceara; caponga; barra da caponga"
            />
            <p className="mt-1 text-xs text-gray-500">
              Use separators: comma, semicolon or pipe. This will be used to filter units and calendars.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <textarea
            name="content"
            value={formData.content}
            onChange={handleChange}
            className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200 min-h-[120px]"
            placeholder="Write clear, evergreen facts."
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Keywords (comma/semicolon separated)
            </label>
            <input
              name="keywords"
              value={formData.keywords}
              onChange={handleChange}
              className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200"
              placeholder="caponga; ceara; beach; chalet"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reference (slug)
            </label>
            <input
              name="reference"
              value={formData.reference}
              onChange={handleChange}
              className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200"
              placeholder="caponga-chale-01"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link URL (Website, Airbnb)
            </label>
            <input
              name="linkUrl"
              value={formData.linkUrl}
              onChange={handleChange}
              className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200"
              placeholder="https://your-site.com/booking/..."
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            iCal URL (optional)
          </label>
          <input
            name="icalUrl"
            value={formData.icalUrl}
            onChange={handleChange}
            className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200"
            placeholder="https://calendar.airbnb.com/ical/..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone (IANA)
            </label>

            <div className="flex items-center gap-4 mb-2">
              <label className="inline-flex items-center gap-1 text-xs text-gray-700">
                <input
                  type="radio"
                  name="timezoneMode"
                  value="preset"
                  checked={formData.timezoneMode === 'preset'}
                  onChange={handleChange}
                  className="h-3 w-3"
                />
                <span>Use preset list</span>
              </label>
              <label className="inline-flex items-center gap-1 text-xs text-gray-700">
                <input
                  type="radio"
                  name="timezoneMode"
                  value="custom"
                  checked={formData.timezoneMode === 'custom'}
                  onChange={handleChange}
                  className="h-3 w-3"
                />
                <span>Custom IANA string</span>
              </label>
            </div>

            {formData.timezoneMode === 'preset' ? (
              <select
                name="timezone"
                value={
                  isTimezoneInPreset(formData.timezone)
                    ? formData.timezone
                    : 'America/Los_Angeles'
                }
                onChange={handleChange}
                className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200"
              >
                {TIMEZONE_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.zones.map((zone) => (
                      <option key={zone} value={zone}>
                        {zone}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            ) : (
              <input
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className="w-full rounded-md border px-3 py-2 text-sm focus:ring focus:ring-blue-200"
                placeholder="Example: America/Los_Angeles, Europe/Athens"
              />
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Flags
            </label>
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="h-4 w-4"
                />
                <span>Active</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="useAI"
                  checked={formData.useAI}
                  onChange={handleChange}
                  className="h-4 w-4"
                />
                <span>Use with AI</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-gray-500">
            {editingId ? 'Editing existing entry' : 'Creating new entry'}
          </div>
          <div className="flex gap-3">
            {editingId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 rounded-md border text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              {editingId ? 'Save changes' : 'Add entry'}
            </button>
          </div>
        </div>
      </form>

      <div className="bg-white rounded-xl shadow-md border">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold text-gray-800">
            Entries ({entries.length})
          </h3>
          {loading && <span className="text-xs text-gray-500">Loading...</span>}
        </div>

        {entries.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">
            No entries yet. Use the form above to create your first one.
          </div>
        ) : (
          <div className="divide-y">
            {entries.map((entry) => {
              const locArr = Array.isArray(entry.locationKeys) ? entry.locationKeys : [];
              const primary =
                normalizeKey(entry.locationKeyPrimary || '') ||
                normalizeKey(entry.locationKey || '');
              const badge = primary || (locArr.length ? normalizeKey(locArr[0]) : '');

              return (
                <div key={entry._id} className="px-4 py-3 text-sm flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{entry.label}</span>

                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {entry.topic}
                        </span>

                        {badge && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 inline-flex items-center gap-1">
                            <MapPin size={12} />
                            {badge}
                          </span>
                        )}

                        {entry.active === false && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">
                            Inactive
                          </span>
                        )}
                        {entry.useAI === false && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                            AI disabled
                          </span>
                        )}
                      </div>

                      <div className="text-gray-700 mt-1 line-clamp-2">{entry.content}</div>
                    </div>

                    <div className="flex flex-shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(entry)}
                        className="inline-flex items-center justify-center rounded-md border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        <Edit2 size={14} className="mr-1" />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(entry._id)}
                        className="inline-flex items-center justify-center rounded-md border px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-1">
                    {entry.reference && (
                      <span className="inline-flex items-center gap-1">
                        <span className="font-semibold">Ref:</span> {entry.reference}
                      </span>
                    )}
                    {entry.linkUrl && (
                      <a
                        href={entry.linkUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <LinkIcon size={12} />
                        Link
                      </a>
                    )}
                    {entry.icalUrl && (
                      <a
                        href={entry.icalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <LinkIcon size={12} />
                        iCal
                      </a>
                    )}
                    {Array.isArray(entry.keywords) && entry.keywords.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <Tag size={12} />
                        {entry.keywords.join(', ')}
                      </span>
                    )}
                    {Array.isArray(entry.locationKeys) && entry.locationKeys.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} />
                        {entry.locationKeys.join(', ')}
                      </span>
                    )}
                    {entry.timezone && (
                      <span className="inline-flex items-center gap-1">
                        <span className="font-semibold">TZ:</span> {entry.timezone}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {allLocationKeys.length > 0 && (
        <div className="mt-6 text-xs text-gray-500">
          Known location keys: {allLocationKeys.join(', ')}
        </div>
      )}
    </div>
  );
};

export default InformativePanel;
